import React, { useState, useEffect, useCallback } from 'react';
import { RECEIPT_TERMINAL_STATUSES } from '../utils/statusConstants';
import { formatCurrency } from '../utils/formatCurrency';
import { 
  Package, 
  Plus, 
  Send, 
  Edit2, 
  Trash2, 
  Clock,
  CheckCircle,
  Truck,
  Home,
  X,
  MessageSquare,
  Bot,
  Users,
  FileText,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  Box,
  Bell,
  BellOff,
  AlertCircle,
  LogOut,
  RotateCcw,
  Settings,
  Loader2,
  FileDown,
  Phone,
  Star,
  Target,
  Globe
} from 'lucide-react';
import { isOnlineOrder, onlineOrderLabel } from '../utils/orderSource';
import ReviewsTab from './ReviewsTab';
import DeliveriesTab from './DeliveriesTab';
import ChatTab from './ChatTab';
import PhoneAgentTab from './PhoneAgentTab';
import SalesPipelineTab from './sales/SalesPipelineTab';
import OrdersQuickStats from './OrdersQuickStats';
import { supabase } from '../supabaseClient';
import { decryptStorePassword } from '../utils/storeCredentialsCrypto';
import { STORED_MULTI_STORE_CREDENTIALS_KEY } from '../utils/storeCredentialsCrypto';

/**
 * ReceiptDashboardMobile - Mobile UI Component
 * 
 * Receives all state and handlers from useReceiptDashboard hook via props.
 * Contains only the mobile-specific UI (lg:hidden section).
 */

/**
 * MobileReceiptCardSkeleton — pulsing placeholder mirroring the real order
 * card's anatomy (chips row, name/phone, status pill, price column, AI/You
 * toggle, action buttons). Rendered in place of the list while receipts
 * load, so the page keeps its outline and cards populate in place —
 * replaces the old full-screen MobileLoadingSplash.
 */
const MobileReceiptCardSkeleton = () => (
  <div
    className="w-full rounded-2xl p-4 bg-white border border-slate-200 animate-pulse"
    aria-hidden="true"
  >
    {/* Status chips row */}
    <div className="flex items-center gap-1.5 mb-3">
      <div className="h-5 w-20 rounded-full bg-slate-100" />
      <div className="h-5 w-16 rounded-full bg-slate-100" />
    </div>
    {/* Name / phone / status pill + price column */}
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="h-4 w-32 rounded bg-slate-200 mb-2" />
        <div className="h-3 w-24 rounded bg-slate-100 mb-3" />
        <div className="flex items-center gap-3">
          <div className="h-6 w-28 rounded-lg bg-slate-100" />
          <div className="h-3 w-12 rounded bg-slate-100" />
        </div>
      </div>
      <div className="flex flex-col items-end ml-4">
        <div className="h-5 w-16 rounded bg-slate-200" />
        <div className="h-3 w-10 rounded bg-slate-100 mt-2" />
        <div className="h-3 w-20 rounded bg-slate-100 mt-2" />
      </div>
    </div>
    {/* Footer: chat toggle row + action buttons */}
    <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center justify-between">
        <div className="h-3 w-36 rounded bg-slate-100" />
        <div className="h-7 w-24 rounded-full bg-slate-100" />
      </div>
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-12 rounded-xl bg-slate-100" />
        <div className="flex-1 h-12 rounded-xl bg-slate-100" />
        <div className="w-12 h-12 rounded-xl bg-slate-100" />
      </div>
    </div>
  </div>
);

const ReceiptDashboardMobile = (props) => {
  // Destructure all needed props from the dashboard hook
  const {
    // State
    receipts,
    loading,
    searchQuery,
    setSearchQuery,
    receiptPeriodFilter,
    setReceiptPeriodFilter,
    receiptSelectedMonth,
    setReceiptSelectedMonth,
    activeTab,
    setActiveTab,
    activeView,
    setActiveView,
    tabVisibility,
    visibleTabCount,
    stores,
    currentStore,
    setCurrentStore,
    unreadMessages,
    escalations,
    allInventoryUnits,
    sendingSMSReceiptId,
    smsSentReceipts,
    
    // Computed values
    activeReceipts,
    newOrdersReceipts,
    completedOrdersReceipts,
    completedTabNotifications,
    stats,
    
    // Handlers
    loadReceipts,
    loadInventoryUnits,
    loadStoreSettings,
    handleOpenReceipt,
    handleOpenModal,
    handleToggleChatMode = () => {},
    togglingChatModeId = null,
    handleSendSMS,
    handleDeleteReceipt,
    openLogoutConfirm,
    setShowStoreSettings,
    setShowSalesTaxExportModal,
    normalizeStatus,
    getStatusIcon,
    getItemReadinessByQuantity,
    readinessByReceiptId,
    getPaymentStatus,
    
    // Constants
    deliveryStatuses,
    
    // Notifications
    addNotification,
    
    // Push Notifications
    pushSupported,
    pushEnabled,
    pushLoading,
    togglePush,

    // Phone Agent
    phoneAgentSettings = {},
    // Voice save + visit/lead status + older-calls pagination — built in
    // useReceiptDashboard (phoneAgentTabProps) and forwarded via {...dashboard}.
    // Mirrors the desktop wiring exactly.
    phoneAgentTabProps = {},
  } = props;

  // ===== Phone Agent Knowledge =====
  // Loads { hours, qa, customInstructions } from /api/phone-agents-2/knowledge
  // and provides a save callback for the Agent Knowledge drawer inside
  // PhoneAgentTab. Mirrors the desktop wiring exactly.
  //
  // Auth: knowledge.py uses sms_security (session-based). We pull the
  // Supabase access_token at request time and send it as a Bearer header,
  // matching the pattern used elsewhere (e.g. /api/push/dismiss).
  const [phoneAgentKnowledge, setPhoneAgentKnowledge] = useState(null);

  // Removed surfaces (July 2026): inventory and campaigns are out of the
  // product on a phone (contacts has no phone view yet). Bounce any stale
  // deep link or restored session URL to Orders. Deliveries is back for the
  // Testing Store only: for every other store tabVisibility.deliveries is
  // false and the hook's hidden-tab fallback bounces it, with a second check
  // here at the view layer.
  useEffect(() => {
    if (
      activeView === 'inventory' ||
      activeView === 'campaigns' ||
      activeView === 'contacts' ||
      (activeView === 'deliveries' && tabVisibility.deliveries !== true)
    ) {
      setActiveView('orders');
    }
  }, [activeView, setActiveView, tabVisibility.deliveries]);

  const handleStoreChange = useCallback((nextStoreId) => {
    const nextStore = stores.find(
      (store) => String(store?.id) === String(nextStoreId),
    );
    if (!nextStore?.id) return;

    setCurrentStore(nextStore);
    localStorage.setItem('selectedStoreId', String(nextStore.id));
    loadReceipts(nextStore.id);
    loadInventoryUnits(nextStore.id);
    loadStoreSettings(nextStore.id);
  }, [stores, setCurrentStore, loadReceipts, loadInventoryUnits, loadStoreSettings]);

  useEffect(() => {
    if (!currentStore?.id) {
      setPhoneAgentKnowledge(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          console.warn('No Supabase session — skipping knowledge load');
          return;
        }
        const res = await fetch(
          `/api/phone-agents-2/knowledge?store_id=${currentStore.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && data?.knowledge) {
          setPhoneAgentKnowledge(data.knowledge);
        }
      } catch (err) {
        console.error('Load phone agent knowledge failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStore?.id]);

  const handleSavePhoneAgentKnowledge = useCallback(
    async (next) => {
      if (!currentStore?.id) throw new Error('No store selected');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const res = await fetch('/api/phone-agents-2/knowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ store_id: currentStore.id, ...next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (data?.knowledge) setPhoneAgentKnowledge(data.knowledge);
    },
    [currentStore?.id]
  );

  // ===== Phone Agent Current Deals =====
  // Mirrors the desktop wiring exactly. See ReceiptDashboardDesktop.jsx for
  // the rationale comment block — keeping the same pattern so both surfaces
  // stay in lockstep when the API or shapes evolve.
  const [phoneAgentDeals, setPhoneAgentDeals] = useState([]);

  const fetchPhoneAgentDeals = useCallback(async () => {
    if (!currentStore?.id) {
      setPhoneAgentDeals([]);
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch(
        `/api/phone-agents-2/current-deals?store_id=${currentStore.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPhoneAgentDeals(Array.isArray(data?.deals) ? data.deals : []);
    } catch (err) {
      console.error('Load current deals failed:', err);
    }
  }, [currentStore?.id]);

  useEffect(() => {
    fetchPhoneAgentDeals();
  }, [fetchPhoneAgentDeals]);

  const handleUploadDealImage = useCallback(
    async (file) => {
      if (!currentStore?.id) throw new Error('No store selected');
      if (!file) throw new Error('No file');
      const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
      const path = `${currentStore.id}/deals/${Date.now()}.${ext || 'jpg'}`;
      const { error: uploadError } = await supabase.storage
        .from('flyers')
        .upload(path, file, {
          contentType: file.type || 'image/jpeg',
          cacheControl: '3600',
          upsert: false,
        });
      if (uploadError) {
        if (String(uploadError.message || '').toLowerCase().includes('not found')) {
          throw new Error(
            "Storage bucket 'flyers' is missing. Create it in Supabase Storage with public access."
          );
        }
        throw uploadError;
      }
      const { data: urlData } = supabase.storage.from('flyers').getPublicUrl(path);
      const publicUrl = urlData?.publicUrl;
      if (!publicUrl) throw new Error('Could not resolve public URL after upload.');
      return publicUrl;
    },
    [currentStore?.id]
  );

  const handleCreateCurrentDeal = useCallback(
    async ({ thumbnail_url, description, title }) => {
      if (!currentStore?.id) throw new Error('No store selected');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const res = await fetch('/api/phone-agents-2/current-deals', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          thumbnail_url,
          description,
          title,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      fetchPhoneAgentDeals();
      return data?.deal || null;
    },
    [currentStore?.id, fetchPhoneAgentDeals]
  );

  const handleDeleteCurrentDeal = useCallback(
    async (id, hard = false) => {
      if (!currentStore?.id) throw new Error('No store selected');
      if (!id) throw new Error('Missing deal id');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const qs = new URLSearchParams({ store_id: currentStore.id, id });
      if (hard) qs.set('hard', '1');
      const res = await fetch(`/api/phone-agents-2/current-deals?${qs.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Delete failed (HTTP ${res.status})`);
      }
      fetchPhoneAgentDeals();
    },
    [currentStore?.id, fetchPhoneAgentDeals]
  );

  // ===== Phone Agent Notification Recipients =====
  // Loads notification_recipients from /api/phone-agents-2/config (the array
  // of {id, name, phone} that gets texted on every call). Same auth pattern
  // as knowledge (Supabase Bearer token). The GET endpoint auto-promotes
  // legacy single-number stores into a one-row array so the UI shows
  // existing data even before the user touches anything.
  //
  // The same /api/phone-agents-2/config response also carries transfer_contacts
  // (the call-forwarding directory). We populate both states from the single
  // fetch below — no need for a parallel useEffect.
  const [phoneAgentRecipients, setPhoneAgentRecipients] = useState([]);
  const [phoneAgentTransferContacts, setPhoneAgentTransferContacts] = useState([]);
  // Kill-switch state, same as the desktop shell: is_active comes back on
  // the /config GET below. Default true so the toggle reads "live" until the
  // load proves otherwise.
  const [phoneAgentActive, setPhoneAgentActive] = useState(true);

  useEffect(() => {
    if (!currentStore?.id) {
      setPhoneAgentRecipients([]);
      setPhoneAgentTransferContacts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          console.warn('No Supabase session — skipping recipients load');
          return;
        }
        const res = await fetch(
          `/api/phone-agents-2/config?store_id=${currentStore.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setPhoneAgentRecipients(
            Array.isArray(data?.notification_recipients)
              ? data.notification_recipients
              : []
          );
          setPhoneAgentTransferContacts(
            Array.isArray(data?.transfer_contacts)
              ? data.transfer_contacts
              : []
          );
          // Only an explicit false means paused; missing/true reads as live.
          setPhoneAgentActive(data?.is_active !== false);
        }
      } catch (err) {
        console.error('Load phone agent recipients failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStore?.id]);

  // ===== Phone Agent kill switch (mirrors ReceiptDashboardDesktop) =====
  // Flips phone_agent_settings.is_active via the same /config POST the other
  // phone-agent saves use. PhoneAgentTab does its own optimistic flip and
  // rollback on throw, so this only needs to throw on failure.
  const handleTogglePhoneAgent = useCallback(
    async (nextEnabled) => {
      if (!currentStore?.id) throw new Error('No store selected');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const res = await fetch('/api/phone-agents-2/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ store_id: currentStore.id, is_active: nextEnabled }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      setPhoneAgentActive(
        typeof data?.is_active === 'boolean' ? data.is_active : nextEnabled
      );
    },
    [currentStore?.id]
  );

  const handleSavePhoneAgentRecipients = useCallback(
    async (cleaned) => {
      if (!currentStore?.id) throw new Error('No store selected');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const res = await fetch('/api/phone-agents-2/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          notification_recipients: cleaned,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (Array.isArray(data?.notification_recipients)) {
        // Update local state with the server-normalized version (E.164 phones,
        // deduped, capped) so the UI reflects exactly what was persisted.
        setPhoneAgentRecipients(data.notification_recipients);
      }
    },
    [currentStore?.id]
  );

  // ===== Phone Agent Transfer Contacts (Call Forwarding) =====
  // Mirrors the recipients save handler exactly. POSTs to the same
  // /api/phone-agents-2/config endpoint — the backend will run the contacts
  // through coerce_transfer_contacts (name+phone required, E.164 normalized,
  // deduped, capped at 10) and return the server-normalized array back.
  const handleSavePhoneAgentTransferContacts = useCallback(
    async (cleaned) => {
      if (!currentStore?.id) throw new Error('No store selected');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not signed in — please refresh and try again');
      const res = await fetch('/api/phone-agents-2/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: currentStore.id,
          transfer_contacts: cleaned,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      if (Array.isArray(data?.transfer_contacts)) {
        // Server-normalized list (E.164 phones, deduped, name-collisions
        // disambiguated). Reflect exactly what was persisted.
        setPhoneAgentTransferContacts(data.transfer_contacts);
      }
    },
    [currentStore?.id]
  );

  const [showEscalations, setShowEscalations] = useState(false);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [storedCredentials, setStoredCredentials] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [switchError, setSwitchError] = useState('');

  const loadStoredCredentials = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(STORED_MULTI_STORE_CREDENTIALS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setStoredCredentials(Array.isArray(parsed) ? parsed : []);
    } catch {
      setStoredCredentials([]);
    }
  }, []);

  useEffect(() => {
    loadStoredCredentials();
  }, [loadStoredCredentials]);

  useEffect(() => {
    const syncUser = async () => {
      const { data } = await supabase.auth.getUser();
      setCurrentUserEmail(data?.user?.email || '');
    };
    syncUser();
    const { data: authSub } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserEmail(session?.user?.email || '');
    });
    return () => authSub?.subscription?.unsubscribe?.();
  }, []);

  useEffect(() => {
    loadStoredCredentials();
  }, [currentUserEmail, loadStoredCredentials]);

  const handleCredentialPick = useCallback(async (credential) => {
    setSwitchError('');
    const pin = window.prompt('Enter your switching PIN');
    if (!pin) return;

    setIsSwitchingAccount(true);
    try {
      const password = await decryptStorePassword(
        credential.encryptedPassword,
        credential.iv,
        pin,
      );
      const { error } = await supabase.auth.signInWithPassword({
        email: credential.email,
        password,
      });
      if (error) throw error;

      loadStoredCredentials();
      setShowStorePicker(false);
      window.location.reload();
    } catch (error) {
      setSwitchError(error?.message || 'Failed to switch store account.');
    } finally {
      setIsSwitchingAccount(false);
    }
  }, [loadStoredCredentials]);

  const hasStoreSwitchOptions = storedCredentials.length > 1 || stores.length > 1;
  const filteredActiveReceipts = !showEscalations
    ? activeReceipts
    : activeReceipts.filter((receipt) => escalations[receipt.id] !== undefined);


  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* PREMIUM MOBILE EXPERIENCE - DARK MODE */}
      <div className="lg:hidden fixed inset-0 bg-slate-100 flex flex-col overflow-hidden">
        {/* Full-bleed status bar background - iOS PWA fix */}
        <div 
          className="flex-shrink-0 bg-slate-100" 
          style={{ 
            height: 'env(safe-area-inset-top, 0px)',
            minHeight: '0px'
          }} 
        />
        {/* Premium Minimal Header - Clean & Modern */}
        <div className="flex-shrink-0 bg-slate-100/95 backdrop-blur-xl border-b border-slate-200 z-50">
          <div className="px-4 py-3">
            <div className="flex flex-row items-center justify-between gap-3">
              {/* Left: Logo & Store */}
              <div className="flex items-center space-x-3 min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => hasStoreSwitchOptions && setShowStorePicker(true)}
                  className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-blue-500/10 flex-shrink-0 active:scale-95 transition-transform"
                  aria-label="Open store switcher"
                >
                  <img src="/e.png" alt="Exitium" className="w-full h-full object-cover" />
                </button>
                <div className="min-w-0">
                  {hasStoreSwitchOptions ? (
                    <button
                      type="button"
                      onClick={() => setShowStorePicker(true)}
                      className="text-base text-slate-900 font-semibold truncate max-w-[180px] text-left active:opacity-80"
                      aria-label="Switch store"
                    >
                      {currentStore?.name || 'Select store'}
                    </button>
                  ) : (
                    <span className="text-base text-slate-900 font-semibold truncate">{currentStore?.name || 'Dashboard'}</span>
                  )}
                </div>
              </div>

              {/* Right: Single Settings/Actions button — opens a sheet with
                  Notifications, Download Taxes, Store Settings, and Log Out */}
              <button
                type="button"
                onClick={() => setShowActionsMenu(true)}
                className="relative w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center active:scale-95 transition-all flex-shrink-0"
                aria-label="Open settings menu"
              >
                <Settings className="w-[18px] h-[18px] text-slate-500" />
                {/* Small indicator dot when push notifications are off so the
                    user can still see notification state at a glance. */}
                {pushSupported && !pushEnabled && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 border border-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sticky Page Title + Search (for Orders & Deliveries) */}
        <div className="flex-shrink-0 bg-slate-100 px-4 pt-3 pb-2 border-b border-slate-200">
          {/* Page Title */}
          <div className={activeView === 'orders' ? 'mb-2' : ''}>
            <h2 className="text-xl font-bold text-slate-900">
              {activeView === 'orders' ? (
                <>Order <span className="text-blue-600">Receipts</span></>
              ) : activeView === 'reviews' ? (
                <>Customer <span className="text-blue-600">Reviews</span></>
              ) : activeView === 'sales' ? (
                <>Sales <span className="text-blue-600">Pipeline</span></>
              ) : activeView === 'deliveries' ? (
                <>Delivery <span className="text-blue-600">Routes</span></>
              ) : activeView === 'chat' ? (
                <>Customer <span className="text-blue-600">Chat</span></>
              ) : (
                <>Phone <span className="text-blue-600">Agent</span></>
              )}
            </h2>
            <p className="text-slate-500 text-xs mt-0.5">
              {activeView === 'orders' 
                ? 'Manage customer orders and deliveries'
                : activeView === 'reviews'
                ? 'Collect customer feedback and grow your reputation'
                : activeView === 'sales'
                ? 'The stores we are selling to and what is due today'
                : activeView === 'deliveries'
                ? 'Plan routes and dispatch drivers'
                : activeView === 'chat'
                ? 'Every customer in one place'
                : 'AI agent that answers your store phone 24/7'
              }
            </p>
          </div>
          
          {/* Orders New/Completed segmented control */}
          {activeView === 'orders' && tabVisibility.orders && (
            <div className="flex gap-1 p-1 bg-slate-200/70 rounded-xl mt-2">
              <button
                type="button"
                onClick={() => setActiveTab('new')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'new'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'bg-transparent text-slate-600 active:bg-slate-300/40'
                }`}
              >
                <Clock className="w-4 h-4" />
                Active
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === 'new' ? 'bg-blue-100 text-blue-600' : 'bg-slate-300/60 text-slate-600'
                }`}>
                  {newOrdersReceipts.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('completed')}
                className={`relative flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === 'completed'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'bg-transparent text-slate-600 active:bg-slate-300/40'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                Completed
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === 'completed' ? 'bg-blue-100 text-blue-600' : 'bg-slate-300/60 text-slate-600'
                }`}>
                  {completedOrdersReceipts.length}
                </span>
                {activeTab !== 'completed' &&
                  (completedTabNotifications.escalationCount > 0 ||
                   completedTabNotifications.newMessageCount > 0) && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full" />
                )}
              </button>
            </div>
          )}

          {/* Search Bar - Only for Orders */}
          {activeView === 'orders' && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="search"
                  name="receipt-search-mobile"
                  autoComplete="off"
                  data-form-type="other"
                  placeholder="Search by name, phone, or item..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 rounded-xl bg-white border border-slate-300 text-slate-900 placeholder-slate-400 text-base focus:outline-none focus:border-blue-400 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 active:bg-slate-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              {activeView === 'orders' && tabVisibility.orders && (
                <div className={`space-y-2 transition-opacity ${searchQuery.trim() ? 'opacity-40 pointer-events-none' : ''}`} aria-disabled={!!searchQuery.trim()}>
                  <div
                    className="flex gap-2 overflow-x-auto no-scrollbar pb-0.5"
                    role="group"
                    aria-label="Filter orders by time period"
                  >
                    {[
                      { id: 'all', label: 'All' },
                      { id: 'week', label: 'This week' },
                      { id: 'month', label: 'Month' },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        disabled={!!searchQuery.trim()}
                        onClick={() => setReceiptPeriodFilter(id)}
                        className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all border ${
                          receiptPeriodFilter === id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-slate-600 border-slate-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {receiptPeriodFilter === 'month' && !searchQuery.trim() && (
                    <label className="block">
                      <span className="sr-only">Choose month</span>
                      <input
                        type="month"
                        value={receiptSelectedMonth}
                        onChange={(e) => setReceiptSelectedMonth(e.target.value)}
                        className="w-full max-w-[220px] rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 focus:outline-none focus:border-blue-400"
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content Area - SCROLLABLE — the actual scrolling viewport for
            every tab. Orders gets its padding here; Reviews and Phone Agent
            manage their own internal layout. */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className={
            activeView === 'orders' && tabVisibility.orders
              ? "px-4 pt-3 pb-4"
              : ""
          }>

          {/* ==========================================
              MOBILE ORDERS VIEW
          ========================================== */}
          {activeView === 'orders' && tabVisibility.orders && (
            <>
              {/* Orders List */}
              {/* Cross-tab search hint: current tab has no matches, but the other tab does. */}
              {!loading && searchQuery.trim() && activeReceipts.length === 0 &&
                ((activeTab === 'new' && completedOrdersReceipts.length > 0) ||
                 (activeTab === 'completed' && newOrdersReceipts.length > 0)) && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 mb-3">
                  <span className="text-sm font-medium text-blue-800">
                    {(activeTab === 'new' ? completedOrdersReceipts.length : newOrdersReceipts.length)}{' '}
                    match{(activeTab === 'new' ? completedOrdersReceipts.length : newOrdersReceipts.length) === 1 ? '' : 'es'} in{' '}
                    {activeTab === 'new' ? 'Completed' : 'Active'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveTab(activeTab === 'new' ? 'completed' : 'new')}
                    className="flex-shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white active:bg-blue-700"
                  >
                    View
                  </button>
                </div>
              )}
              {loading ? (
                /* Page-outline loading: the header, tabs, and search above
                   already render — these skeletons hold the list's shape
                   until receipts arrive, then real cards populate in place. */
                <div
                  className="space-y-3"
                  role="status"
                  aria-live="polite"
                  aria-label="Loading orders"
                >
                  <MobileReceiptCardSkeleton />
                  <MobileReceiptCardSkeleton />
                  <MobileReceiptCardSkeleton />
                  <MobileReceiptCardSkeleton />
                </div>
              ) : activeReceipts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-slate-50 flex items-center justify-center mb-4">
                    {searchQuery ? (
                      <Search className="w-10 h-10 text-gray-600" />
                    ) : receiptPeriodFilter !== 'all' ? (
                      <Filter className="w-10 h-10 text-gray-600" />
                    ) : (
                      <Package className="w-10 h-10 text-gray-600" />
                    )}
                  </div>
                  <p className="text-slate-500 font-medium">
                    {searchQuery
                      ? 'No matching orders'
                      : receiptPeriodFilter !== 'all'
                        ? 'No orders in this time range'
                        : 'No orders found'}
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    {searchQuery
                      ? `No results for "${searchQuery}"`
                      : receiptPeriodFilter !== 'all'
                        ? 'Try a different period or show all orders.'
                        : activeTab === 'new'
                          ? 'New orders will appear here'
                          : 'Completed orders appear here'}
                  </p>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-4 px-5 py-3 bg-blue-100 text-blue-600 rounded-xl text-sm font-medium active:bg-blue-200"
                    >
                      Clear Search
                    </button>
                  )}
                  {!searchQuery && receiptPeriodFilter !== 'all' && (
                    <button
                      type="button"
                      onClick={() => setReceiptPeriodFilter('all')}
                      className="mt-4 px-5 py-3 bg-blue-100 text-blue-600 rounded-xl text-sm font-medium active:bg-blue-200"
                    >
                      Show all orders
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredActiveReceipts.map((receipt) => {
                    const status = normalizeStatus(receipt.deliveryStatus || receipt.delivery_status);
                    const customerName = receipt.customerName || receipt.customer_name;
                    const customerPhone = receipt.customerPhone || receipt.customer_phone;
                    const deliveryDate = receipt.deliveryDate || receipt.delivery_date;
                    const createdAt = receipt.createdAt || receipt.created_at;
                    const sentAt = receipt.sentAt || receipt.sent_at;
                    
                    // Format created date
                    const createdDate = createdAt ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
                    
                    // Check for unread messages from notification system
                    const unreadCount = unreadMessages[receipt.id] || 0;
                    const hasUnread = unreadCount > 0 || (receipt.hasActiveChat && receipt.unreadMessages > 0);
                    const displayUnread = unreadCount || receipt.unreadMessages || 0;
                    
                    // Calculate inventory readiness using inventory_units (accounts for quantities)
                    // PERF: Use precomputed readiness map (O(1) lookup) instead of recomputing
                    // per receipt every render. Falls back to the original function if the
                    // map doesn't have this receipt yet (e.g. just-added receipt mid-render),
                    // which guarantees identical behavior to the previous implementation.
                    const readiness = readinessByReceiptId?.[receipt.id]
                      || getItemReadinessByQuantity(receipt, allInventoryUnits);
                    const { totalQty, deliveredQty, pickedUpQty, readyQty, outForDeliveryQty, waitingQty, allReady, allDelivered, hasAnyProgress } = readiness;
                    
                    // For backwards compatibility with existing UI code
                    const totalItems = totalQty;
                    const deliveredCount = deliveredQty - (pickedUpQty || 0); // Only actual deliveries
                    const pickedUpCount = pickedUpQty || 0;
                    const readyCount = readyQty;
                    const outForDeliveryCount = outForDeliveryQty;
                    const waitingCount = waitingQty;
                    
                    const allOutForDelivery = totalItems > 0 && outForDeliveryCount === totalItems;
                    const isOutForDelivery = status === 'out_for_delivery' || allOutForDelivery;
                    const isPartialOutForDelivery = status === 'partial_out_for_delivery' || (outForDeliveryCount > 0 && outForDeliveryCount < totalItems);
                    
                    // Check for escalation on this receipt
                    const escalation = escalations[receipt.id];
                    const hasEscalation = escalation && !escalation.acknowledged;
                    const isOnline = isOnlineOrder(receipt);
                    
                    return (
                      <button
                        key={receipt.id}
                        onClick={() => handleOpenReceipt(receipt)}
                        data-tip="Opens this receipt"
                        className={`w-full rounded-2xl p-4 text-left transition-all active:scale-[0.98] relative ${
                          hasEscalation
                            ? 'bg-red-50 border-2 border-red-500 shadow-lg shadow-red-500/30 escalation-card'
                            : hasUnread && allReady && status !== 'delivered' && status !== 'picked_up'
                            ? 'bg-emerald-50 border-2 border-emerald-400 shadow-lg shadow-emerald-500/10'
                            : hasUnread
                            ? 'bg-red-50 border-2 border-red-300 shadow-lg shadow-red-500/10'
                            : isOutForDelivery && status !== 'delivered' && status !== 'picked_up'
                            ? 'bg-cyan-50 border-2 border-cyan-300 shadow-lg shadow-cyan-500/10'
                            : isPartialOutForDelivery && status !== 'delivered' && status !== 'picked_up'
                            ? 'bg-blue-50 border-2 border-blue-300 shadow-lg shadow-blue-500/10'
                            : allReady && status !== 'delivered' && status !== 'picked_up'
                            ? 'bg-emerald-50 border-2 border-emerald-300 shadow-lg shadow-emerald-500/10'
                            : isOnline
                            ? 'bg-white border-2 border-violet-400 shadow-lg shadow-violet-500/10'
                            : 'bg-white border border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {/* ONLINE ORDER - full-width title strip so it stands out */}
                        {isOnline && (
                          <div className="-mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-2xl bg-violet-600 text-white flex items-center gap-2">
                            <Globe className="w-4 h-4 flex-shrink-0" />
                            <span className="text-[13px] font-black uppercase tracking-wide truncate">{onlineOrderLabel(receipt)}</span>
                          </div>
                        )}

                        {/* ESCALATION Indicator - Blinking Red Badge */}
                        {hasEscalation && (
                          <div className="absolute -top-2 -right-2 flex items-center space-x-1 bg-red-500 text-white px-2.5 py-1 rounded-full shadow-lg escalation-badge">
                            <AlertCircle className="w-3 h-3" />
                            <span className="text-xs font-bold">URGENT</span>
                          </div>
                        )}
                        
                        {/* New Message Indicator - only show if no escalation */}
                        {hasUnread && !hasEscalation && (
                          <div className="absolute -top-2 -right-2 flex items-center space-x-1 bg-red-500 text-white px-2.5 py-1 rounded-full shadow-lg">
                            <MessageSquare className="w-3 h-3" />
                            <span className="text-xs font-bold">{displayUnread} new</span>
                          </div>
                        )}
                        
                        {/* Out for Delivery Badge */}
                        {isOutForDelivery && status !== 'delivered' && status !== 'picked_up' && (
                          <div className="flex items-center space-x-1.5 mb-3">
                            <Truck className="w-3.5 h-3.5 text-blue-600" />
                            <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Out for Delivery</span>
                          </div>
                        )}
                        
                        {/* Partial Out for Delivery Badge */}
                        {isPartialOutForDelivery && !isOutForDelivery && status !== 'delivered' && status !== 'picked_up' && (
                          <div className="flex items-center space-x-1.5 mb-3">
                            <Truck className="w-3.5 h-3.5 text-blue-500" />
                            <span className="text-xs font-semibold text-blue-500 uppercase tracking-wide">
                              {outForDeliveryCount} of {totalItems} Out for Delivery
                            </span>
                          </div>
                        )}
                        
                        {/* Ready Badge - all items ready to ship */}
                        {allReady && !isOutForDelivery && !isPartialOutForDelivery && status !== 'delivered' && status !== 'picked_up' && (
                          <div className="flex items-center space-x-1.5 mb-3">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">Ready to Ship</span>
                          </div>
                        )}
                        
                        {/* Item Status Breakdown - Always show when there are items */}
                        {totalItems > 0 && (
                          <div className="flex items-center flex-wrap gap-1.5 mb-3">
                            {deliveredCount > 0 && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300">
                                <Home className="w-3 h-3 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700">{deliveredCount} Delivered</span>
                              </span>
                            )}
                            {pickedUpCount > 0 && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-300">
                                <Box className="w-3 h-3 text-emerald-600" />
                                <span className="text-xs font-semibold text-emerald-700">{pickedUpCount} Picked Up</span>
                              </span>
                            )}
                            {outForDeliveryCount > 0 && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-indigo-100 border border-indigo-300">
                                <Truck className="w-3 h-3 text-indigo-600" />
                                <span className="text-xs font-semibold text-indigo-700">{outForDeliveryCount} On Truck</span>
                              </span>
                            )}
                            {readyCount > 0 && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-green-100 border border-green-300">
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                <span className="text-xs font-semibold text-green-700">{readyCount} Ready</span>
                              </span>
                            )}
                            {waitingCount > 0 && (
                              <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300">
                                <Clock className="w-3 h-3 text-slate-500" />
                                <span className="text-xs font-semibold text-slate-600">{waitingCount} Waiting</span>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Main Content */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="font-semibold text-slate-900 truncate">{customerName}</h3>
                              {receipt.salesperson && (
                                <span className="text-xs text-slate-500">• {receipt.salesperson}</span>
                              )}
                            </div>
                            <p className="text-slate-500 text-sm mb-3">{customerPhone}</p>
                            
                            {/* Status & Info Row - uses inventory-based effective status */}
                            <div className="flex items-center space-x-3">
                              {(() => {
                                // Calculate effective status from inventory tracking (source of truth)
                                const effectiveStatus = 
                                  allDelivered ? 'delivered' :
                                  isOutForDelivery ? 'out_for_delivery' :
                                  isPartialOutForDelivery ? 'partial_out_for_delivery' :
                                  status === 'partially_delivered' ? 'partially_delivered' :
                                  'pending';
                                
                                const statusColors = {
                                  delivered: 'bg-emerald-100 text-emerald-600',
                                  out_for_delivery: 'bg-indigo-100 text-indigo-600',
                                  partial_out_for_delivery: 'bg-indigo-100 text-indigo-600',
                                  partially_delivered: 'bg-amber-100 text-amber-600',
                                  pending: 'bg-blue-100 text-blue-600'
                                };
                                
                                return (
                                  <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${statusColors[effectiveStatus]}`}>
                                    {getStatusIcon(effectiveStatus)}
                                    <span>{deliveryStatuses.find(s => s.value === effectiveStatus)?.label || 'Order Received'}</span>
                                  </span>
                                );
                              })()}
                              <span className="text-gray-600 text-xs">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          
                          {/* Right Side - Price, Payment & Arrow */}
                          <div className="flex flex-col items-end ml-4">
                            <p className="text-slate-900 font-bold text-lg">${formatCurrency(receipt.total)}</p>
                            {/* Payment Status Indicator */}
                            {(() => {
                              const paymentInfo = getPaymentStatus(receipt);
                              if (paymentInfo.status === 'paid') {
                                return (
                                  <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 mt-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Paid
                                  </span>
                                );
                              } else if (paymentInfo.status === 'partial') {
                                return (
                                  <span className="text-xs font-semibold text-amber-400 mt-1">
                                    ${formatCurrency(paymentInfo.balance)} due
                                  </span>
                                );
                              } else if (receipt.total > 0) {
                                return (
                                  <span className="text-xs font-semibold text-red-600 mt-1">
                                    Unpaid
                                  </span>
                                );
                              }
                              return null;
                            })()}
                            <p className="text-slate-500 text-xs mt-1">
                              {createdDate && <span>Ordered {createdDate}</span>}
                            </p>
                            <ChevronRight className="w-5 h-5 text-gray-600 mt-1" />
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-200">
                          {/* Chat Mode Toggle - Full Width */}
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-500">Customer chat handled by:</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleToggleChatMode(receipt, e); }}
                                disabled={togglingChatModeId === receipt.id}
                                data-tip={receipt.is_ai_handling !== false ? "AI is auto-replying. Tap to take over manually" : "You're replying manually. Tap to hand back to AI"}
                                className="flex items-center gap-0.5 p-0.5 rounded-full bg-slate-100 border border-slate-300 transition-all disabled:opacity-60"
                              >
                                {togglingChatModeId === receipt.id ? (
                                  <div className="flex items-center gap-1.5 px-4 py-1.5">
                                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-500" />
                                  </div>
                                ) : (
                                  <>
                                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full transition-all ${
                                      receipt.is_ai_handling !== false
                                        ? 'bg-violet-500 text-white shadow-sm' 
                                        : 'text-slate-400'
                                    }`}>
                                      <Bot className="w-3.5 h-3.5" />
                                      <span className="text-xs font-semibold">AI</span>
                                    </div>
                                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full transition-all ${
                                      receipt.is_ai_handling === false
                                        ? 'bg-amber-500 text-white shadow-sm' 
                                        : 'text-slate-400'
                                    }`}>
                                      <Users className="w-3.5 h-3.5" />
                                      <span className="text-xs font-semibold">You</span>
                                    </div>
                                  </>
                                )}
                              </button>
                            </div>
                            <p className="text-[11px] text-slate-400">
                              {receipt.is_ai_handling !== false
                                ? 'AI is automatically responding to this customer\'s messages' 
                                : 'You are manually responding to this customer\'s messages'}
                            </p>
                          </div>
                          
                          {/* Action Buttons Row */}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSendSMS(receipt); }}
                              disabled={sendingSMSReceiptId === receipt.id || receipt.sms_opted_out}
                              data-tip={receipt.sms_opted_out ? "Customer opted out of SMS, so nothing can be sent" : sentAt ? "Texts the customer their receipt link again" : "Texts the customer their receipt link"}
                              className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                                receipt.sms_opted_out
                                  ? 'bg-red-50 border border-red-300 text-red-500'
                                  : smsSentReceipts[receipt.id]
                                  ? 'bg-emerald-100 border border-emerald-500/30 text-emerald-600'
                                  : sentAt
                                  ? 'bg-blue-500/10 border border-blue-500/20 text-blue-500 active:bg-blue-500/20'
                                  : 'bg-blue-50 border border-blue-200 text-blue-600 active:bg-blue-100'
                              }`}
                            >
                              {receipt.sms_opted_out ? (
                                <>
                                  <AlertCircle className="w-4 h-4" />
                                  <span>Opted Out</span>
                                </>
                              ) : sendingSMSReceiptId === receipt.id ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  <span>Sending...</span>
                                </>
                              ) : smsSentReceipts[receipt.id] ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Sent ✓</span>
                                </>
                              ) : sentAt ? (
                                <>
                                  <RotateCcw className="w-4 h-4" />
                                  <span>Resend</span>
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4" />
                                  <span>Send SMS</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleOpenModal(receipt); }}
                              data-tip="Opens this receipt's details for editing"
                              className="flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-sm font-semibold active:bg-slate-100"
                            >
                              <Edit2 className="w-4 h-4" />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteReceipt(receipt.id); }}
                              aria-label="Delete receipt"
                              className="w-12 h-12 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center text-red-600 active:bg-red-100"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

      {
        showEscalations && filteredActiveReceipts.length === 0 && (
          <p className="text-slate-500 font-medium">
          {searchQuery ? 'No matching orders' : 'No escalated orders found'}
        </p>
        )
      }
          {/* ==========================================
              PREMIUM FLOATING ACTION BUTTON - Orders Only
          ========================================== */}
          {activeView === 'orders' && tabVisibility.orders && (
            <button
              onClick={() => handleOpenModal()}
              aria-label="New receipt"
              className="fixed z-40 right-5 group active:scale-95 transition-transform"
              style={{ 
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 130px)'
              }}
            >
              {/* Ambient glow */}
              <div className="absolute inset-0 bg-blue-600 rounded-full blur-lg opacity-40 group-active:opacity-60 transition-opacity" />
              
              {/* Main button */}
              <div className="relative w-14 h-14 flex items-center justify-center bg-blue-600 rounded-full shadow-lg shadow-blue-500/10">
                <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
            </button>
          )}

          {/* ==========================================
              MOBILE DELIVERIES VIEW — Testing Store only (deliveriesAccess.js).
              Strict === true: never mounts for a live store.
          ========================================== */}
          {activeView === 'deliveries' && tabVisibility.deliveries === true && (
            <DeliveriesTab
              storeId={currentStore?.id}
              store={currentStore}
              receipts={receipts}
              hideHeader={true}
              onRefresh={() => {
                loadReceipts(currentStore?.id);
                loadInventoryUnits(currentStore?.id);
              }}
              addNotification={addNotification}
            />
          )}

          {/* ==========================================
              MOBILE CHAT VIEW — Testing Store only (chatAccess.js).
              Strict === true: never mounts for a live store.
          ========================================== */}
          {activeView === 'chat' && tabVisibility.chat === true && (
            <div className="h-full min-h-0">
              <ChatTab
                storeId={currentStore?.id}
                store={currentStore}
                addNotification={addNotification}
                onOpenReceipt={(r) => handleOpenReceipt(r)}
                hideHeader={true}
              />
            </div>
          )}

          {/* ==========================================
              MOBILE REVIEWS VIEW
          ========================================== */}
          {activeView === 'reviews' && tabVisibility.reviews !== false && (
            <ReviewsTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
              hideHeader={true}
            />
          )}

          {/* ==========================================
              MOBILE SALES VIEW — Exitium's own pipeline. Strict === true:
              Testing Store on the owner login only (salesPipelineAccess.js).
          ========================================== */}
          {activeView === 'sales' && tabVisibility.sales === true && (
            <SalesPipelineTab
              storeId={currentStore?.id}
              store={currentStore}
              addNotification={addNotification}
              hideHeader={true}
            />
          )}

          {/* ==========================================
              MOBILE PHONE AGENT VIEW
          ========================================== */}
          {activeView === 'phoneAgent' && (tabVisibility.phoneAgent !== false) && (
            <PhoneAgentTab
              key={`pat-mobile-${currentStore?.id || 'no-store'}-${phoneAgentKnowledge ? 'k' : 'pending'}-${phoneAgentRecipients.length}-${phoneAgentTransferContacts.length}-${phoneAgentDeals.length}`}
              currentStore={currentStore}
              addNotification={addNotification}
              agentNumber={phoneAgentSettings?.bland_phone_number || ''}
              agentEnabled={phoneAgentActive}
              onToggleAgent={handleTogglePhoneAgent}
              initialRecipients={phoneAgentRecipients}
              onSaveRecipients={handleSavePhoneAgentRecipients}
              initialTransferContacts={phoneAgentTransferContacts}
              onSaveTransferContacts={handleSavePhoneAgentTransferContacts}
              initialKnowledge={phoneAgentKnowledge || undefined}
              onSaveKnowledge={handleSavePhoneAgentKnowledge}
              initialCurrentDeals={phoneAgentDeals}
              onUploadCurrentDeal={handleUploadDealImage}
              onCreateCurrentDeal={handleCreateCurrentDeal}
              onDeleteCurrentDeal={handleDeleteCurrentDeal}
              onRefreshCurrentDeals={fetchPhoneAgentDeals}
              onFetchCalls={async () => {
                if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return [];
                const res = await fetch(
                  `/api/phone-agents-2/calls?store_id=${currentStore.id}&limit=50`,
                  { headers: { 'X-Phone-Agent-Key': phoneAgentSettings.tool_auth_token } }
                );
                if (!res.ok) throw new Error(`Failed to fetch calls: ${res.status}`);
                const data = await res.json();
                return data.calls || [];
              }}
              onFetchLiveCalls={async () => {
                if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return [];
                const res = await fetch(
                  `/api/phone-agents-2/calls?store_id=${currentStore.id}&live=1`,
                  { headers: { 'X-Phone-Agent-Key': phoneAgentSettings.tool_auth_token } }
                );
                if (!res.ok) return [];
                const data = await res.json();
                return data.live || [];
              }}
              onFetchCallDetail={async (callId) => {
                if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) return null;
                const res = await fetch(
                  `/api/phone-agents-2/calls?store_id=${currentStore.id}&call_id=${encodeURIComponent(callId)}`,
                  { headers: { 'X-Phone-Agent-Key': phoneAgentSettings.tool_auth_token } }
                );
                if (!res.ok) throw new Error(`Failed to fetch call: ${res.status}`);
                const data = await res.json();
                return data.call || null;
              }}
              onMarkCallReviewed={async (callId) => {
                if (!currentStore?.id || !phoneAgentSettings?.tool_auth_token) {
                  throw new Error('Not ready');
                }
                const res = await fetch('/api/phone-agents-2/calls', {
                  method: 'PATCH',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Phone-Agent-Key': phoneAgentSettings.tool_auth_token,
                  },
                  body: JSON.stringify({
                    call_id: callId,
                    store_id: currentStore.id,
                    reviewed: true,
                  }),
                });
                if (!res.ok) throw new Error(`Failed to mark reviewed: ${res.status}`);
                return res.json();
              }}
              onOpenReceipt={(receiptId) => {
                if (!receiptId) return;
                const receipt = (receipts || []).find((r) => r.id === receiptId);
                if (receipt) {
                  handleOpenReceipt(receipt);
                } else {
                  handleOpenReceipt({ id: receiptId });
                }
              }}
              {...phoneAgentTabProps}
            />
          )}
          </div>
        </div>

        {/* ==========================================
            BOTTOM NAVIGATION - Mobile Optimized
        ========================================== */}
        <div className="flex-shrink-0 relative z-50">
          <div className="relative bg-white border-t border-slate-200">
            <div className="px-6" style={{ paddingTop: '6px', paddingBottom: 'calc(6px + env(safe-area-inset-bottom, 0px))' }}>
              <div className="flex items-center justify-around">
                
                {(tabVisibility.phoneAgent !== false) && (
                <button
                  type="button"
                  disabled={
                    visibleTabCount === 1 && activeView === 'phoneAgent'
                  }
                  onClick={() => {
                    setActiveView('phoneAgent');
                    setSearchQuery('');
                  }}
                  data-tip="Your AI phone line: call log, follow-ups, transfers, voice and catalog"
                  className="flex flex-col items-center group py-0 disabled:cursor-default disabled:opacity-100"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'phoneAgent'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <Phone className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'phoneAgent' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'phoneAgent' ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'phoneAgent' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Phone AI</span>
                </button>
                )}

                {/* Orders — not interactive when it is the only tab */}
                {tabVisibility.orders && (
                <button
                  type="button"
                  disabled={
                    visibleTabCount === 1 && activeView === 'orders'
                  }
                  onClick={() => {
                    setActiveView('orders');
                    loadReceipts(currentStore?.id, true); // silent: refresh in place, no skeletons
                  }}
                  data-tip="Receipts: item status, payments, delivery and customer texts"
                  className="flex flex-col items-center group py-0 disabled:cursor-default disabled:opacity-100"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'orders'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <FileText className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'orders' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'orders' ? 2 : 1.5} />
                    
                    {receipts.filter(r => !RECEIPT_TERMINAL_STATUSES.includes(r.deliveryStatus || r.delivery_status)).length > 0 && (
                      <div className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-[9px] font-bold text-white leading-none">
                          {receipts.filter(r => !RECEIPT_TERMINAL_STATUSES.includes(r.deliveryStatus || r.delivery_status)).length}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'orders' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Orders</span>
                </button>
                )}

                {/* Chat — Testing Store only (chatAccess.js) */}
                {tabVisibility.chat === true && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveView('chat');
                    setSearchQuery('');
                  }}
                  data-tip="Every customer's texts in one place"
                  className="flex flex-col items-center group py-0"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'chat'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <MessageSquare className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'chat' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'chat' ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'chat' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Chat</span>
                </button>
                )}

                {/* Deliveries — Testing Store only */}
                {tabVisibility.deliveries === true && (
                <button
                  type="button"
                  disabled={
                    visibleTabCount === 1 && activeView === 'deliveries'
                  }
                  onClick={() => {
                    setActiveView('deliveries');
                    setSearchQuery('');
                    loadReceipts(currentStore?.id);
                  }}
                  data-tip="Plan routes and dispatch drivers"
                  className="flex flex-col items-center group py-0 disabled:cursor-default disabled:opacity-100"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'deliveries'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <Truck className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'deliveries' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'deliveries' ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'deliveries' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Deliveries</span>
                </button>
                )}

                {/* Reviews — independent bottom nav tab */}
                {(tabVisibility.reviews !== false) && (
                <button
                  type="button"
                  disabled={
                    visibleTabCount === 1 && activeView === 'reviews'
                  }
                  onClick={() => {
                    setActiveView('reviews');
                    setSearchQuery('');
                  }}
                  data-tip="Customer ratings and Google review requests"
                  className="flex flex-col items-center group py-0 disabled:cursor-default disabled:opacity-100"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'reviews'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <Star className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'reviews' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'reviews' ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'reviews' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Reviews</span>
                </button>
                )}

                {/* Sales — Testing Store on the owner login only */}
                {tabVisibility.sales === true && (
                <button
                  type="button"
                  onClick={() => {
                    setActiveView('sales');
                    setSearchQuery('');
                  }}
                  data-tip="Exitium's pipeline: the stores we are selling to and what is due today"
                  className="flex flex-col items-center group py-0"
                >
                  <div className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                    activeView === 'sales'
                      ? 'bg-blue-600'
                      : 'bg-transparent'
                  }`}>
                    <Target className={`w-[22px] h-[22px] transition-colors ${
                      activeView === 'sales' ? 'text-white' : 'text-slate-500'
                    }`} strokeWidth={activeView === 'sales' ? 2 : 1.5} />
                  </div>
                  <span className={`text-[10px] font-medium mt-0.5 transition-colors ${
                    activeView === 'sales' ? 'text-blue-600' : 'text-slate-500'
                  }`}>Sales</span>
                </button>
                )}


                
              </div>
            </div>
          </div>
        </div>

        {showStorePicker && hasStoreSwitchOptions && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-end" onClick={() => setShowStorePicker(false)}>
            <div
              className="w-full bg-white rounded-t-2xl p-4 max-h-[60vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-sm font-semibold text-slate-500 mb-3">Switch store</div>
              <div className="space-y-2">
                {storedCredentials.length > 0 ? (
                  storedCredentials.map((credential) => {
                    const isCurrent = credential.email === currentUserEmail;
                    return (
                      <button
                        key={credential.email}
                        type="button"
                        disabled={isSwitchingAccount}
                        onClick={() => handleCredentialPick(credential)}
                        className={`w-full text-left px-3 py-3 rounded-xl border transition-colors disabled:opacity-60 ${
                          isCurrent
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-white border-slate-200 text-slate-800 active:bg-slate-50'
                        }`}
                      >
                        {credential.email}
                      </button>
                    );
                  })
                ) : (
                  stores.map((store) => {
                    const isCurrent = String(store?.id) === String(currentStore?.id);
                    return (
                      <button
                        key={store.id}
                        type="button"
                        onClick={() => {
                          handleStoreChange(store.id);
                          setShowStorePicker(false);
                        }}
                        className={`w-full text-left px-3 py-3 rounded-xl border transition-colors ${
                          isCurrent
                            ? 'bg-blue-50 border-blue-200 text-blue-700'
                            : 'bg-white border-slate-200 text-slate-800 active:bg-slate-50'
                        }`}
                      >
                        {store.name}
                      </button>
                    );
                  })
                )}
              </div>
              {switchError && (
                <div className="mt-3 text-xs text-red-600">{switchError}</div>
              )}
            </div>
          </div>
        )}

        {/* Mobile-only actions menu (bottom sheet).
            Hosts Notifications, Download Taxes, Store Settings, Log Out —
            previously rendered as 4 separate header buttons. */}
        {showActionsMenu && (
          <div
            className="fixed inset-0 z-[70] bg-black/40 flex items-end"
            onClick={() => setShowActionsMenu(false)}
          >
            <div
              className="w-full bg-white rounded-t-2xl p-4 max-h-[60vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <div className="text-sm font-semibold text-slate-500 mb-3">Settings</div>
              <div className="space-y-2">
                {/* Notifications toggle — preserves original disabled/loading
                    behavior and only renders when push is supported. */}
                {pushSupported && (
                  <button
                    type="button"
                    onClick={togglePush}
                    disabled={pushLoading}
                    data-tip={pushEnabled ? "Turns off push notifications on this device" : "Turns on push notifications on this device"}
                    className="w-full flex items-center justify-between px-3 py-3 rounded-xl border bg-white border-slate-200 text-slate-800 active:bg-slate-50 transition-colors disabled:opacity-60"
                  >
                    <span className="flex items-center gap-3">
                      {pushLoading ? (
                        <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                      ) : pushEnabled ? (
                        <Bell className="w-5 h-5 text-green-600" />
                      ) : (
                        <BellOff className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="text-sm font-medium">Notifications</span>
                    </span>
                    <span
                      className={`text-xs font-semibold ${
                        pushEnabled ? 'text-green-600' : 'text-slate-400'
                      }`}
                    >
                      {pushEnabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowSalesTaxExportModal(true);
                  }}
                  data-tip="Opens the sales and tax CSV export"
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border bg-white border-slate-200 text-slate-800 active:bg-slate-50 transition-colors"
                >
                  <FileDown className="w-5 h-5 text-slate-500" />
                  <span className="text-sm font-medium">Download Taxes (CSV)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    setShowStoreSettings(true);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border bg-white border-slate-200 text-slate-800 active:bg-slate-50 transition-colors"
                >
                  <Settings className="w-5 h-5 text-slate-500" />
                  <span className="text-sm font-medium">Store Settings</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowActionsMenu(false);
                    openLogoutConfirm();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl border bg-white border-slate-200 text-red-600 active:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium">Log Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceiptDashboardMobile;